import { Activity, useRef, type ReactNode } from "react";
import { useOutlet } from "react-router";

export interface CachedOutletProps {
    cacheKey: string;
    retainedKeys: string[];
}

/**
 * 缓存已经打开的路由页面。
 *
 * React Activity 隐藏页面时保留组件状态和 DOM，重新显示后会恢复筛选、排序、分页及表格滚动位置。
 */
export function CachedOutlet({ cacheKey, retainedKeys }: CachedOutletProps) {
    const outlet = useOutlet();
    const cachedOutletsRef = useRef(new Map<string, ReactNode>());
    const retainedKeySet = new Set([...retainedKeys, cacheKey]);

    // 当前路由始终使用最新 Router Context；相同 cacheKey 的组件实例由 Activity 保持。
    cachedOutletsRef.current.set(cacheKey, outlet);
    for (const cachedKey of cachedOutletsRef.current.keys()) {
        if (!retainedKeySet.has(cachedKey)) {
            cachedOutletsRef.current.delete(cachedKey);
        }
    }

    return [...cachedOutletsRef.current.entries()].map(([cachedKey, cachedOutlet]) => (
        <Activity key={cachedKey} mode={cachedKey === cacheKey ? "visible" : "hidden"}>
            {cachedOutlet}
        </Activity>
    ));
}

export default CachedOutlet;
